import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationFacade, PurchaseService } from '@ridango/components';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import Env from '@ridango/core/env';
import {
    ActivationType,
    CategoryProduct,
    NextButtonElement,
    Product,
    ProductArea
} from '@ridango/components/lib/purchase/purchase.model';
import { takeUntil } from 'rxjs/operators';
import { AppStartupService, BaseComponent } from '@ridango/core';
import { ModalController } from '@ionic/angular';
import moment from 'moment/moment';

@Component({
    selector: 'gl-purchase-v2-ticket-activation',
    templateUrl: './activation.component.html',
    styleUrls: ['./activation.component.scss']
})
export class ActivationComponent extends BaseComponent implements OnInit, OnDestroy {
    @ViewChildren('areas') areasQueryList: QueryList<ElementRef>;
    @Output() nextButtonElement = new EventEmitter<NextButtonElement>();

    @Input() productAreas: ProductArea[];
    @Input() isShowInnerNextButton = false;
    @Input() isJourneyPlannerPurchase = false;
    @Input() categoryProducts: CategoryProduct[] = [];

    @Output() nextStep = new EventEmitter<void>();
    @Output() backStep = new EventEmitter<void>();

    public assetsPath$ = this.applicationFacade.assetsPath$;
    currentLanguage = this.translate.currentLang;
    public products: Product[] = [];
    public ActivationType = ActivationType;
    selectedActivationType: ActivationType = ActivationType.AFTER_PURCHASE;
    activationMenuElements: ActivationType[] = [ActivationType.AFTER_PURCHASE, ActivationType.BEFORE_PURCHASE];
    shouldShowActivationUI = false;
    private isBackwardNavigation = false;
    activationDateTime = { datetime: moment().format() };
    private timezone: string = this.appStartupService.getTimezone();
    minDate: string = moment().tz(this.timezone).format();
    maxDate: string = moment().add(14, 'day').format();
    public currency: string;
    public chosenAreasFromPreviousStep: ProductArea[];

    constructor(
        @Inject('ENV') public env: Env,
        private translate: TranslateService,
        private purchaseService: PurchaseService,
        private activatedRoute: ActivatedRoute,
        private location: Location,
        private modalCtrl: ModalController,
        private applicationFacade: ApplicationFacade,
        private router: Router,
        public appStartupService: AppStartupService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.purchaseService.setPurchaseDataLoading(false);
        this.purchaseService.emitSelectActivationMethod(this.selectedActivationType);
        this.checkForImmediateAutoSkip();

        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.currentLanguage = event.lang;
        });

        if (this.purchaseService.getStepDataIdsByStepName('product')) {
            const categoryProductId = this.purchaseService.getStepDataIdsByStepName('category-product')[0];

            const allProductsOnThisAreaAndCategory = this.categoryProducts.find(
                (c) => c.id === categoryProductId
            ).products;

            const selectedticketTypes = this.purchaseService
                .getStepDataIdsByStepName('product')
                .filter((item) => item.length > 2); // FIXME: Potential bug as if amount of people > 99(digits > 2) it will read that amount as product id
            const selectedProductsIds = selectedticketTypes.map(
                (productIdWithParameter) => productIdWithParameter.split(':')[0]
            );

            this.products = allProductsOnThisAreaAndCategory.filter(function (item) {
                return selectedProductsIds.indexOf(item.id) !== -1;
            });

            this.generateActivationMethodsBasedOnTickets(this.products);

            if (!this.isShowInnerNextButton) {
                this.nextButtonElement.emit({
                    content: {
                        currency: this.currency,
                        textTerm: 'CONTINUE_TO_CHECKOUT',
                        amount: this.getTotalAmount()
                    },
                    disabled: false
                });
            }
        }

        this.currency = this.appStartupService.getCurrency();

        if (!this.isJourneyPlannerPurchase) {
            const productAreaIds = this.purchaseService.getStepDataIdsByStepName('product-area');
            this.chosenAreasFromPreviousStep = this.productAreas?.filter((pa: ProductArea) => {
                return typeof pa.id === 'string' && productAreaIds.length === 1
                    ? decodeURIComponent(productAreaIds) === pa.id
                    : productAreaIds.includes(pa.id);
            });
        }

        if (this.purchaseService.getStepDataIdsByStepName('ticket-activation')) {
            if (this.purchaseService.getStepDataIdsByStepName('ticket-activation')[0]) {
                const result = this.purchaseService.getStepDataIdsByStepName('ticket-activation')[0];
                this.typeSelected(result === 'AFTER_PURCHASE' ? ActivationType.AFTER_PURCHASE : result);
            } else {
                this.typeSelected(this.selectedActivationType);
            }
            if (this.purchaseService.getStepDataIdsByStepName('ticket-activation')[1]) {
                await this.setActivationDateTime({
                    datetime: moment(this.purchaseService.getStepDataIdsByStepName('ticket-activation')[1]).format(
                        'YYYY-MM-DD'
                    )
                });
            }
        }

        if (!this.isShowInnerNextButton) {
            this.nextButtonElement.emit({
                content: { currency: this.currency, textTerm: 'CONTINUE_TO_CHECKOUT', amount: this.getTotalAmount() },
                disabled: false
            });
        }
    }

    async typeSelected(type) {
        const purchaseFlowRoute = this.isJourneyPlannerPurchase ? ['journey-planner', 'purchase'] : ['purchase-v2'];
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        const isRecentFlow = urlSegments.includes('recent'); // Check BEFORE splicing
        urlSegments.splice(urlSegments.indexOf('ticket-activation') + 1);

        this.selectedActivationType = type;
        this.purchaseService.emitSelectActivationMethod(this.selectedActivationType);

        if (type === ActivationType.BEFORE_PURCHASE) {
            await this.setActivationDateTime();
        } else {
            const navigationSegments = [
                this.translate.currentLang,
                ...purchaseFlowRoute,
                ...urlSegments,
                this.selectedActivationType
            ];
            if (isRecentFlow) {
                navigationSegments.push('recent');
            }
            await this.router.navigate(navigationSegments);
        }
    }

    async setActivationDateTime($event?: { datetime: string; type?: 'leaveAfter' | 'arriveBy' }) {
        const purchaseFlowRoute = this.isJourneyPlannerPurchase ? ['journey-planner', 'purchase'] : ['purchase-v2'];
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        urlSegments.splice(urlSegments.indexOf('ticket-activation') + 2);

        if (this.activationDateTime.datetime) {
            urlSegments.pop();
        }
        if ($event) {
            this.activationDateTime = $event;
        }
        const navigationSegments = [
            this.translate.currentLang,
            ...purchaseFlowRoute,
            ...urlSegments,
            this.selectedActivationType,
            moment(this.activationDateTime.datetime).format('YYYY-MM-DD')
        ];
        const isRecentFlow = this.activatedRoute.snapshot.url.some((segment) => segment.path === 'recent');
        if (isRecentFlow) {
            navigationSegments.push('recent');
        }
        await this.router.navigate(navigationSegments);
    }

    generateActivationMethodsBasedOnTickets(products: Product[]): void {
        const activationOrder = Object.values(ActivationType);
        const triggers = products
            .flatMap((item) => {
                if (item.productParameters?.availableParameters?.activations) {
                    return item.productParameters.availableParameters.activations.map(
                        (method) => method.activationTrigger
                    ) as ActivationType[];
                }

                return item.activationMethod.map((method) => method.activationTrigger) as ActivationType[];
            })
            .sort((a, b) => {
                return activationOrder.indexOf(a) - activationOrder.indexOf(b);
            });

        this.activationMenuElements = [...new Set(triggers)];
        this.selectedActivationType = this.activationMenuElements[0];

        if (this.activationMenuElements.length < 2) {
            this.shouldShowActivationUI = false;
            if (this.isBackwardNavigation) {
                this.autoSkipBackward();
            } else {
                this.autoSkipToNextStep();
            }
        } else {
            this.shouldShowActivationUI = true;
        }
    }

    private checkForImmediateAutoSkip(): void {
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        const activationIndex = urlSegments.indexOf('ticket-activation');

        if (activationIndex !== -1 && urlSegments[activationIndex + 1]) {
            const urlActivationType = urlSegments[activationIndex + 1];
            this.isBackwardNavigation = true;

            switch (urlActivationType) {
                case 'PURCHASE':
                    this.selectedActivationType = ActivationType.AFTER_PURCHASE;
                    break;
                case 'MANUAL':
                    this.selectedActivationType = ActivationType.MANUAL;
                    break;
                case 'SPECIFIED_ACTIVATION_DATE':
                    this.selectedActivationType = ActivationType.BEFORE_PURCHASE;
                    break;
            }

            this.shouldShowActivationUI = false;
        }
    }

    private async autoSkipBackward(): Promise<void> {
        this.purchaseService.emitSelectActivationMethod(this.selectedActivationType);
        this.purchaseService.setPurchaseDataLoading(true);

        if (this.isJourneyPlannerPurchase) {
            const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
            const activationIndex = urlSegments.indexOf('ticket-activation');

            if (activationIndex !== -1) {
                urlSegments.splice(activationIndex);
            }

            const targetUrl = `/${this.translate.currentLang}/journey-planner/purchase/${urlSegments.join('/')}`;
            await this.router.navigateByUrl(targetUrl, { replaceUrl: true });
        } else {
            this.backStep.emit();
        }
    }

    private async autoSkipToNextStep(): Promise<void> {
        this.purchaseService.emitSelectActivationMethod(this.selectedActivationType);
        if (this.selectedActivationType === ActivationType.BEFORE_PURCHASE) {
            await this.setActivationDateTime();
        } else {
            this.handleNextClicked();
        }
    }

    ticketEnd(str) {
        return str.includes('30')
            ? moment(this.activationDateTime.datetime).add(30, 'day')
            : moment(this.activationDateTime.datetime).add(1, 'day');
    }

    getTotalAmount() {
        const selectionsInUrl = this.purchaseService.getStepDataIdsByStepName('product');
        const selectedTravellerTypes = selectionsInUrl.filter((item) => item.length > 2);
        const selectedProductIds = selectedTravellerTypes.map(
            (productIdWithParameter) => productIdWithParameter.split(':')[0]
        );

        return this.products
            .filter(function (item) {
                return selectedProductIds.indexOf(item.id) !== -1;
            })
            .map((product, index) => {
                this.currency = product.price.currency;

                const isSingleTicketGroup = product.userProfiles?.[0]?.isGroup;
                if (isSingleTicketGroup) {
                    let totalCostOfSelectedTypes = 0;
                    for (const [key, part] of selectionsInUrl.entries()) {
                        if (Number(key) % 2 === 0) {
                            // if current part from url is a productTravellerTypeId, not amount
                            const travellerTypeId = part;
                            const [productId, paramCode] = travellerTypeId.split(':');
                            const selectedAmount = selectionsInUrl[key + 1];
                            const travellerType = this.products[0].userProfiles[0].companionProfiles.find(
                                (cp) => cp.dynamicProductParameters[0].code === paramCode
                            );
                            if (productId === product.id && paramCode && selectedAmount && travellerType) {
                                const costOfThisType =
                                    travellerType.perPersonPrice.amount ?? travellerType.perPersonPrice.maxAmount;
                                totalCostOfSelectedTypes += costOfThisType * selectedAmount;
                            }
                        }
                    }
                    return totalCostOfSelectedTypes;
                }
                // for NEW AFC - 68167-186/3/68177-22/1/68182-137/1/68172-150/1/, so index should be multiplied x2
                const productIndex = this.env.IS_NEW_AFC ? index * 2 + 1 : index + 1;
                return (
                    this.purchaseService.getStepDataIdsByStepName('product')[productIndex] *
                    this.purchaseService.defineProductPriceByActivationMethod(product).amount
                );
            })
            .reduce((a, b) => a + b, 0);
    }

    handleNextClicked() {
        this.purchaseService.emitActivationUrl(
            this.location.path().replace(ActivationType.AFTER_PURCHASE, 'AFTER_PURCHASE')
        );
        this.nextStep.emit();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
